import React, { useState, useEffect, useMemo } from 'react';
import BackButton from '@/components/BackButton';
import EmployeeAvatar from '@/components/EmployeeAvatar';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useLanguage } from '@/contexts/LanguageContext';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { ChartBar, FileText, Download, SpinnerGap, User, MicrosoftExcelLogo, FilePdf, ArrowsClockwise } from '@phosphor-icons/react';
import axios from 'axios';
import { toast } from 'sonner';
import { formatHoursHM } from '@/utils/formatHours';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LOGO_URL = "/branding/logo.png";

export default function AdminReports() {
  const { t } = useTranslation();
  const { language } = useLanguage();
  const { user, isAdmin } = useAuth();
  const isAr = language === 'ar';
  const navigate = useNavigate();
  // v1.6.6: Finance Manager now has access to the Reports page but ONLY
  // the per-employee report (attendance/payroll aggregate reports stay
  // admin-only). We pre-select the "employee" tab and hide the other
  // two cards for non-admin users.
  const isGM = user?.role === 'general_manager' || !!user?.is_general_manager;
  const canSeeAllReports = isAdmin || isGM;
  const [reportType, setReportType] = useState(canSeeAllReports ? 'attendance' : 'employee');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [month, setMonth] = useState(new Date().toISOString().slice(5, 7));
  const [year, setYear] = useState(new Date().getFullYear().toString());
  const [downloading, setDownloading] = useState(false);

  // ── Per-employee report state ─────────────────────────────
  const [employees, setEmployees] = useState([]);
  const [empSearch, setEmpSearch] = useState('');
  const [selectedEmp, setSelectedEmp] = useState(null);
  const [empStart, setEmpStart] = useState('');
  const [empEnd, setEmpEnd] = useState('');
  const [empSummary, setEmpSummary] = useState(null);
  const [loadingSummary, setLoadingSummary] = useState(false);
  const [downloadingEmp, setDownloadingEmp] = useState(''); // 'pdf' | 'excel' | ''

  useEffect(() => {
    if (reportType !== 'employee') return;
    if (employees.length > 0) return;
    axios.get(`${API}/employees`, { withCredentials: true })
      .then(({ data }) => setEmployees(data || []))
      .catch(() => {});
  }, [reportType, employees.length]);

  const filteredEmps = useMemo(() => {
    const q = empSearch.trim().toLowerCase();
    if (!q) return employees.slice(0, 30);
    return employees.filter((e) => {
      const hay = `${e.name || ''} ${e.name_en || ''} ${e.employee_number || ''} ${e.email || ''}`.toLowerCase();
      return hay.includes(q);
    }).slice(0, 30);
  }, [employees, empSearch]);

  const loadEmpSummary = async () => {
    if (!selectedEmp) { toast.error(isAr ? 'اختر موظفاً أولاً' : 'Select an employee first'); return; }
    if (!empStart || !empEnd) { toast.error(isAr ? 'حدد المدى الزمني' : 'Set the date range'); return; }
    if (empEnd < empStart) { toast.error(isAr ? 'تاريخ النهاية قبل البداية' : 'End date before start'); return; }
    setLoadingSummary(true);
    setEmpSummary(null);
    try {
      const { data } = await axios.get(`${API}/reports/employee-summary`, {
        params: { user_id: selectedEmp.id, start_date: empStart, end_date: empEnd },
        withCredentials: true,
      });
      setEmpSummary(data);
    } catch (err) {
      toast.error(err.response?.data?.detail || (isAr ? 'فشل تحميل التقرير' : 'Failed to load report'));
    } finally {
      setLoadingSummary(false);
    }
  };

  const downloadEmpReport = async (format /* pdf | excel */) => {
    if (!selectedEmp || !empStart || !empEnd) return;
    setDownloadingEmp(format);
    try {
      const url = `${API}/reports/employee-summary/${format}?user_id=${selectedEmp.id}&start_date=${empStart}&end_date=${empEnd}&language=${language}`;
      const resp = await fetch(url, { credentials: 'include' });
      if (!resp.ok) throw new Error('download failed');
      const blob = await resp.blob();
      const ext = format === 'pdf' ? 'pdf' : 'xlsx';
      const fname = `employee_report_${selectedEmp.employee_number || selectedEmp.id}_${empStart}_${empEnd}.${ext}`;
      const durl = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = durl; a.download = fname;
      document.body.appendChild(a); a.click();
      window.URL.revokeObjectURL(durl); document.body.removeChild(a);
    } catch {
      toast.error(isAr ? 'فشل التنزيل' : 'Download failed');
    } finally {
      setDownloadingEmp('');
    }
  };

  const handleDownload = async () => {
    setDownloading(true);
    try {
      let url = '';
      let filename = '';

      if (reportType === 'attendance') {
        if (!startDate || !endDate) {
          alert('Please select start and end dates');
          return;
        }
        url = `${API}/reports/attendance?start_date=${startDate}&end_date=${endDate}`;
        filename = `attendance_report_${startDate}_${endDate}.pdf`;
      } else {
        url = `${API}/reports/payroll?month=${month}&year=${year}`;
        filename = `payroll_report_${month}_${year}.pdf`;
      }

      const response = await fetch(url, {
        credentials: 'include'
      });

      if (!response.ok) throw new Error('Failed to download report');

      const blob = await response.blob();
      const downloadUrl = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = downloadUrl;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(downloadUrl);
      document.body.removeChild(a);
    } catch (error) {
      console.error('Failed to download report:', error);
    } finally {
      setDownloading(false);
    }
  };

  const months = [
    { value: '01', label: t('months.01') },
    { value: '02', label: t('months.02') },
    { value: '03', label: t('months.03') },
    { value: '04', label: t('months.04') },
    { value: '05', label: t('months.05') },
    { value: '06', label: t('months.06') },
    { value: '07', label: t('months.07') },
    { value: '08', label: t('months.08') },
    { value: '09', label: t('months.09') },
    { value: '10', label: t('months.10') },
    { value: '11', label: t('months.11') },
    { value: '12', label: t('months.12') },
  ];

  const years = Array.from({ length: 5 }, (_, i) => (new Date().getFullYear() - i).toString());

  return (
    <div className="min-h-screen bg-[#F2F0FA]" data-testid="admin-reports-page">
      {/* Header */}
      <header className="sticky top-0 z-40 bg-white/95 backdrop-blur-sm shadow-sm border-b border-[#C4BFF0] py-4">
        <div className="max-w-[1800px] mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between">
          <img src={LOGO_URL} alt="Lucent" className="h-10 object-contain" />
          <BackButton className="text-[#655dc6]" />
        </div>
      </header>

      {/* Content */}
      <main className="max-w-[1800px] mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-6">
        <h1 className="text-3xl font-semibold text-[#655dc6]">{t('reports')}</h1>

        <div className={`grid grid-cols-1 ${canSeeAllReports ? 'md:grid-cols-3' : 'md:grid-cols-1 max-w-2xl'} gap-6`}>
          {canSeeAllReports && (
          <>
          {/* Attendance Report */}
          <Card 
            className={`border cursor-pointer transition-all duration-200 ${
              reportType === 'attendance' 
                ? 'border-[#655dc6] ring-2 ring-[#655dc6]/20' 
                : 'border-[#C4BFF0] hover:border-[#655dc6]/50'
            }`}
            onClick={() => setReportType('attendance')}
            data-testid="attendance-report-card"
          >
            <CardHeader>
              <CardTitle className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-[#655dc6]">
                  <ChartBar size={24} weight="duotone" className="text-white" />
                </div>
                <span className="text-lg font-medium text-[#655dc6]">{t('attendance_report')}</span>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-[#655dc6]/70 text-sm">
                {isAr
                  ? 'تقرير حضور تفصيلي لجميع الموظفين خلال فترة محددة.'
                  : 'Detailed attendance report with clock-in/out times for all employees.'}
              </p>
            </CardContent>
          </Card>

          {/* Payroll Report */}
          <Card 
            className={`border cursor-pointer transition-all duration-200 ${
              reportType === 'payroll' 
                ? 'border-[#655dc6] ring-2 ring-[#655dc6]/20' 
                : 'border-[#C4BFF0] hover:border-[#655dc6]/50'
            }`}
            onClick={() => setReportType('payroll')}
            data-testid="payroll-report-card"
          >
            <CardHeader>
              <CardTitle className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-[#7B6FD6]">
                  <FileText size={24} weight="duotone" className="text-white" />
                </div>
                <span className="text-lg font-medium text-[#655dc6]">{t('payroll_report')}</span>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-[#655dc6]/70 text-sm">
                {isAr
                  ? 'تقرير رواتب شامل لجميع الموظفين لشهر محدد.'
                  : 'Payroll report with salary details, allowances, and deductions.'}
              </p>
            </CardContent>
          </Card>
          </>
          )}

          {/* Per-Employee Report — NEW */}
          <Card
            className={`border cursor-pointer transition-all duration-200 ${
              reportType === 'employee'
                ? 'border-[#655dc6] ring-2 ring-[#655dc6]/20'
                : 'border-[#C4BFF0] hover:border-[#655dc6]/50'
            }`}
            onClick={() => setReportType('employee')}
            data-testid="employee-report-card"
          >
            <CardHeader>
              <CardTitle className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-[#8B7CD8]">
                  <User size={24} weight="duotone" className="text-white" />
                </div>
                <span className="text-lg font-medium text-[#655dc6]">
                  {isAr ? 'تقرير موظف' : 'Employee Report'}
                </span>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-[#655dc6]/70 text-sm">
                {isAr
                  ? 'حضور وغياب وساعات إضافية لموظف واحد خلال فترة محددة.'
                  : 'Attendance, absences and overtime for a single employee over a period.'}
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Report Parameters */}
        <Card className="border border-[#C4BFF0]">
          <CardHeader>
            <CardTitle className="text-lg font-medium text-[#655dc6]">
              {reportType === 'attendance' && (isAr ? 'معطيات تقرير الحضور' : 'Attendance Parameters')}
              {reportType === 'payroll' && (isAr ? 'معطيات تقرير الرواتب' : 'Payroll Parameters')}
              {reportType === 'employee' && (isAr ? 'معطيات تقرير الموظف' : 'Employee Report Parameters')}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {reportType === 'attendance' && (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 max-w-md">
                <div className="space-y-2">
                  <Label>{t('start_date')}</Label>
                  <Input
                    type="date"
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                    className="bg-[#E8E6F5] border-[#C4BFF0]"
                    data-testid="start-date-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label>{t('end_date')}</Label>
                  <Input
                    type="date"
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                    className="bg-[#E8E6F5] border-[#C4BFF0]"
                    data-testid="end-date-input"
                  />
                </div>
              </div>
            )}
            {reportType === 'payroll' && (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 max-w-md">
                <div className="space-y-2">
                  <Label>{t('select_month')}</Label>
                  <Select value={month} onValueChange={setMonth}>
                    <SelectTrigger className="bg-[#E8E6F5] border-[#C4BFF0]" data-testid="month-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {months.map((m) => (
                        <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>{t('select_year')}</Label>
                  <Select value={year} onValueChange={setYear}>
                    <SelectTrigger className="bg-[#E8E6F5] border-[#C4BFF0]" data-testid="year-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {years.map((y) => (
                        <SelectItem key={y} value={y}>{y}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            )}
            {reportType === 'employee' && (
              <div className="space-y-4">
                {/* Employee picker */}
                <div className="space-y-2">
                  <Label>{isAr ? 'الموظف' : 'Employee'}</Label>
                  {selectedEmp ? (
                    <div
                      className="flex items-center gap-3 p-3 rounded-lg bg-[#E8E6F5] border border-[#655dc6]"
                      data-testid="selected-employee-chip"
                    >
                      <EmployeeAvatar
                        userId={selectedEmp.id}
                        name={selectedEmp.name_en || selectedEmp.name}
                        photoPath={selectedEmp.photo_path}
                        photoUpdatedAt={selectedEmp.photo_updated_at}
                        size={40}
                        fontClass="text-sm"
                      />
                      <div className="flex-1 min-w-0">
                        <p className="font-semibold text-[#655dc6] truncate">{selectedEmp.name}</p>
                        <p className="text-xs text-[#655dc6]/60 truncate">
                          {selectedEmp.employee_number ? `#${selectedEmp.employee_number} · ` : ''}
                          {selectedEmp.department || ''}
                        </p>
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => { setSelectedEmp(null); setEmpSummary(null); }}
                        className="text-[#655dc6] hover:bg-[#DCD8F7]"
                        data-testid="clear-selected-employee"
                      >
                        {isAr ? 'تغيير' : 'Change'}
                      </Button>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      <Input
                        type="search"
                        value={empSearch}
                        onChange={(e) => setEmpSearch(e.target.value)}
                        placeholder={isAr ? 'ابحث بالاسم أو الرقم الوظيفي' : 'Search by name or employee #'}
                        className="bg-[#E8E6F5] border-[#C4BFF0]"
                        data-testid="employee-search-input"
                      />
                      <div className="max-h-64 overflow-y-auto rounded-lg border border-[#C4BFF0] divide-y divide-[#C4BFF0]/60 bg-white">
                        {filteredEmps.length === 0 ? (
                          <div className="p-4 text-center text-sm text-[#655dc6]/50">
                            {isAr ? 'لا توجد نتائج' : 'No matches'}
                          </div>
                        ) : filteredEmps.map((e) => (
                          <button
                            key={e.id}
                            type="button"
                            onClick={() => { setSelectedEmp(e); setEmpSearch(''); }}
                            className="w-full flex items-center gap-3 p-2.5 hover:bg-[#F2F0FA] text-start transition-colors"
                            data-testid={`employee-option-${e.id}`}
                          >
                            <EmployeeAvatar
                              userId={e.id}
                              name={e.name_en || e.name}
                              photoPath={e.photo_path}
                              photoUpdatedAt={e.photo_updated_at}
                              size={32}
                              fontClass="text-xs"
                            />
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium text-[#655dc6] truncate">{e.name}</p>
                              <p className="text-[11px] text-[#655dc6]/60 truncate">
                                {e.employee_number ? `#${e.employee_number} · ` : ''}
                                {e.department || ''}
                              </p>
                            </div>
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                </div>

                {/* Date range */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 max-w-md">
                  <div className="space-y-2">
                    <Label>{isAr ? 'من تاريخ' : 'From date'}</Label>
                    <Input
                      type="date"
                      value={empStart}
                      onChange={(e) => setEmpStart(e.target.value)}
                      className="bg-[#E8E6F5] border-[#C4BFF0]"
                      data-testid="emp-start-date"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>{isAr ? 'إلى تاريخ' : 'To date'}</Label>
                    <Input
                      type="date"
                      value={empEnd}
                      onChange={(e) => setEmpEnd(e.target.value)}
                      className="bg-[#E8E6F5] border-[#C4BFF0]"
                      data-testid="emp-end-date"
                    />
                  </div>
                </div>

                <div className="flex flex-wrap items-center gap-2">
                  <Button
                    onClick={loadEmpSummary}
                    disabled={loadingSummary || !selectedEmp || !empStart || !empEnd}
                    className="bg-[#655dc6] hover:bg-[#544cb0] text-white disabled:opacity-60"
                    data-testid="load-emp-summary-btn"
                  >
                    {loadingSummary
                      ? <SpinnerGap size={16} className="animate-spin me-2" />
                      : <ArrowsClockwise size={16} className="me-2" />}
                    {isAr ? 'عرض التقرير' : 'View report'}
                  </Button>
                  {empSummary && (
                    <>
                      <Button
                        onClick={() => downloadEmpReport('pdf')}
                        disabled={downloadingEmp === 'pdf'}
                        variant="outline"
                        className="border-[#655dc6] text-[#655dc6] hover:bg-[#655dc6]/10 gap-2"
                        data-testid="download-emp-pdf"
                      >
                        {downloadingEmp === 'pdf'
                          ? <SpinnerGap size={16} className="animate-spin" />
                          : <FilePdf size={16} weight="duotone" />}
                        PDF
                      </Button>
                      <Button
                        onClick={() => downloadEmpReport('excel')}
                        disabled={downloadingEmp === 'excel'}
                        variant="outline"
                        className="border-emerald-600 text-emerald-700 hover:bg-emerald-50 gap-2"
                        data-testid="download-emp-excel"
                      >
                        {downloadingEmp === 'excel'
                          ? <SpinnerGap size={16} className="animate-spin" />
                          : <MicrosoftExcelLogo size={16} weight="duotone" />}
                        Excel
                      </Button>
                    </>
                  )}
                </div>
              </div>
            )}

            {reportType !== 'employee' && (
              <Button
                onClick={handleDownload}
                disabled={downloading}
                className="mt-6 bg-[#655dc6] hover:bg-[#544cb0] text-white"
                data-testid="download-report-btn"
              >
                {downloading ? (
                  <SpinnerGap size={20} className="animate-spin me-2" />
                ) : (
                  <Download size={20} className="me-2" />
                )}
                {t('download_report')}
              </Button>
            )}
          </CardContent>
        </Card>

        {/* Employee report results */}
        {reportType === 'employee' && empSummary && (
          <Card className="border border-[#C4BFF0]" data-testid="emp-report-results">
            <CardHeader>
              <CardTitle className="flex items-center justify-between flex-wrap gap-2">
                <span className="text-lg text-[#655dc6]">
                  {isAr ? `تفاصيل حضور: ${empSummary.employee.name}` : `Attendance detail — ${empSummary.employee.name_en || empSummary.employee.name}`}
                </span>
                <span className="text-xs font-normal text-[#655dc6]/60" dir="ltr">
                  {empSummary.range.start_date} → {empSummary.range.end_date}
                </span>
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* KPI grid — v1.5.29 added Cycle days + Rest days so
                  present+absent+leave+rest(+no_record) = cycle days. */}
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2">
                {[
                  { lb_ar: 'أيام الدورة', lb_en: 'Cycle days', v: empSummary.totals.total_days, color: 'slate' },
                  { lb_ar: 'حضور', lb_en: 'Present', v: empSummary.totals.present_days, color: 'emerald' },
                  { lb_ar: 'غياب', lb_en: 'Absent', v: empSummary.totals.absent_days, color: 'red' },
                  { lb_ar: 'إجازة', lb_en: 'On leave', v: empSummary.totals.leave_days, color: 'amber' },
                  { lb_ar: 'راحة', lb_en: 'Rest days', v: empSummary.totals.off_days, color: 'sky' },
                  ...(empSummary.totals.no_record_days
                    ? [{ lb_ar: 'بدون سجل', lb_en: 'No record', v: empSummary.totals.no_record_days, color: 'gray' }]
                    : []),
                  { lb_ar: 'ساعات عمل', lb_en: 'Work hrs', v: formatHoursHM(empSummary.totals.work_hours), color: 'purple' },
                  { lb_ar: 'دقائق تأخير', lb_en: 'Late (min)', v: empSummary.totals.late_minutes, color: 'orange' },
                  { lb_ar: 'إضافي معتمد', lb_en: 'Approved OT', v: formatHoursHM(empSummary.approved_overtime.hours), color: 'blue' },
                ].map((k, i) => (
                  <div key={i} className="rounded-lg border border-[#C4BFF0] bg-[#F2F0FA] p-3 text-center" data-testid={`kpi-${k.lb_en.toLowerCase().replace(/\s/g,'-')}`}>
                    <p className="text-[10px] uppercase tracking-wide text-[#655dc6]/60 font-semibold">
                      {isAr ? k.lb_ar : k.lb_en}
                    </p>
                    <p className="text-xl font-bold text-[#655dc6] mt-0.5">{k.v}</p>
                  </div>
                ))}
              </div>

              {/* Daily rows */}
              <div className="overflow-x-auto rounded-lg border border-[#C4BFF0]">
                <table className="w-full text-sm" data-testid="emp-daily-table">
                  <thead className="bg-[#655dc6] text-white">
                    <tr>
                      <th className="p-2 text-center font-medium">{isAr ? 'التاريخ' : 'Date'}</th>
                      <th className="p-2 text-center font-medium">{isAr ? 'اليوم' : 'Day'}</th>
                      <th className="p-2 text-center font-medium">{isAr ? 'دخول' : 'In'}</th>
                      <th className="p-2 text-center font-medium">{isAr ? 'خروج' : 'Out'}</th>
                      <th className="p-2 text-center font-medium">{isAr ? 'ساعات' : 'Hours'}</th>
                      <th className="p-2 text-center font-medium">{isAr ? 'تأخير(د)' : 'Late(m)'}</th>
                      <th className="p-2 text-center font-medium">{isAr ? 'إضافي(س)' : 'OT(h)'}</th>
                      <th className="p-2 text-center font-medium">{isAr ? 'الحالة' : 'Status'}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {empSummary.rows.map((r, i) => {
                      const stKey = r.status;
                      const stAr = { present: 'حاضر', absent: 'غائب', on_leave: 'إجازة', off: 'راحة', off_day: 'راحة', no_record: '—', excused: 'بعذر', late: 'متأخر', missing_clock_out: 'بدون انصراف' };
                      const stEn = { present: 'Present', absent: 'Absent', on_leave: 'On Leave', off: 'Rest', off_day: 'Rest', no_record: '—', excused: 'Excused', late: 'Late', missing_clock_out: 'Missing OUT' };
                      const stTxt = (isAr ? stAr : stEn)[stKey] || stKey;
                      const stCls = {
                        present: 'bg-emerald-100 text-emerald-800',
                        absent: 'bg-red-100 text-red-800',
                        late: 'bg-orange-100 text-orange-800',
                        missing_clock_out: 'bg-yellow-100 text-yellow-800',
                        on_leave: 'bg-amber-100 text-amber-800',
                        off: 'bg-slate-100 text-slate-600',
                        off_day: 'bg-slate-100 text-slate-600',
                        no_record: 'bg-slate-50 text-slate-400',
                      }[stKey] || 'bg-slate-100 text-slate-600';
                      return (
                        <tr key={i} className={i % 2 === 0 ? 'bg-white' : 'bg-[#F2F0FA]/60'}>
                          <td className="p-2 text-center font-mono text-xs" dir="ltr">{r.date}</td>
                          <td className="p-2 text-center">{r.weekday}</td>
                          <td className="p-2 text-center font-mono text-xs" dir="ltr">{r.clock_in || '—'}</td>
                          <td className="p-2 text-center font-mono text-xs" dir="ltr">{r.clock_out || '—'}</td>
                          <td className="p-2 text-center font-mono">{r.work_hours ? formatHoursHM(r.work_hours) : '—'}</td>
                          <td className="p-2 text-center">{r.late_minutes || '—'}</td>
                          <td className="p-2 text-center font-mono">{r.overtime_hours ? formatHoursHM(r.overtime_hours) : '—'}</td>
                          <td className="p-2 text-center">
                            <span className={`inline-block px-2 py-0.5 rounded-full text-[11px] font-medium ${stCls}`}>
                              {stTxt}
                            </span>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {/* Approved OT breakdown */}
              {empSummary.approved_overtime.entries?.length > 0 && (
                <div>
                  <h3 className="text-sm font-semibold text-[#655dc6] mb-2">
                    {isAr ? 'الساعات الإضافية المعتمدة' : 'Approved Overtime Entries'}
                  </h3>
                  <div className="overflow-x-auto rounded-lg border border-[#C4BFF0]">
                    <table className="w-full text-sm">
                      <thead className="bg-[#655dc6] text-white">
                        <tr>
                          <th className="p-2 text-center font-medium">{isAr ? 'الشهر' : 'Month'}</th>
                          <th className="p-2 text-center font-medium">{isAr ? 'الساعات' : 'Hours'}</th>
                          <th className="p-2 text-center font-medium">{isAr ? 'الأجر/ساعة' : 'Hourly Rate'}</th>
                          <th className="p-2 text-center font-medium">{isAr ? 'قيمة الإضافي' : 'Amount'}</th>
                          <th className="p-2 text-center font-medium">{isAr ? 'المصدر' : 'Source'}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {empSummary.approved_overtime.entries.map((e, i) => (
                          <tr key={i} className={i % 2 === 0 ? 'bg-white' : 'bg-[#F2F0FA]/60'}>
                            <td className="p-2 text-center font-mono text-xs" dir="ltr">{e.year}-{e.month}</td>
                            <td className="p-2 text-center font-mono">{formatHoursHM(e.hours)}</td>
                            <td className="p-2 text-center font-mono">{e.overtime_rate.toFixed(2)}</td>
                            <td className="p-2 text-center font-mono">{e.overtime_amount.toFixed(2)}</td>
                            <td className="p-2 text-center text-xs text-[#655dc6]/70">{e.source || '—'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        )}
      </main>
    </div>
  );
}
